import { Link } from "react-router-dom";
import { Boxes, Flame, ArrowRight, CheckCircle2 } from "@/lib/icons";
import { useEffect, useState } from "react";
import {
  getDailyPuzzle,
  loadResult,
  loadStreak,
  todayKey,
  type DailyPuzzle,
  type DailyResult,
} from "./engine";

/**
 * Compact "Element of the Day" teaser used on the homepage. Shows the
 * element symbol + name (or a teaser if not yet played), today's streak,
 * and a CTA into `/daily`. Hydration-safe: starts in a loading state and
 * only reads localStorage after mount so SSR and first paint match.
 */
export default function DailyElementCard() {
  const [hydrated, setHydrated] = useState(false);
  const [puzzle, setPuzzle] = useState<DailyPuzzle | null>(null);
  const [result, setResult] = useState<DailyResult | null>(null);
  const [streak, setStreak] = useState<{ current: number; best: number }>({
    current: 0,
    best: 0,
  });

  useEffect(() => {
    const key = todayKey();
    const p = getDailyPuzzle(key);
    setPuzzle(p);
    setResult(loadResult(key));
    setStreak(loadStreak());
    setHydrated(true);
  }, []);

  const played = !!result;
  const symbol = puzzle?.element.symbol ?? "?";
  const name = puzzle?.element.name ?? "Loading...";

  return (
    <Link
      to="/daily"
      className="group relative flex items-center gap-4 p-4 rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-accent/30 transition-colors"
      aria-label={`Element of the day: ${played ? name : "play to reveal"}`}
    >
      {/* Symbol tile */}
      <div className="flex-shrink-0 w-16 h-16 rounded-lg border border-primary/30 bg-primary/5 flex flex-col items-center justify-center font-mono">
        {hydrated && played ? (
          <>
            <span className="text-[10px] text-muted-foreground leading-none">
              {puzzle?.element.number}
            </span>
            <span className="text-2xl font-bold text-primary leading-tight">
              {symbol}
            </span>
          </>
        ) : (
          <Boxes
            size={22}
            className="text-primary/70"
            aria-hidden="true"
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
            Daily
          </span>
          {hydrated && streak.current > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-orange-500 dark:text-orange-400">
              <Flame size={10} aria-hidden="true" />
              {streak.current}-day streak
            </span>
          )}
        </div>
        <div className="text-sm font-semibold text-foreground truncate">
          {hydrated && played
            ? `${name} - ${result?.score}/${result?.total}`
            : "Element of the Day"}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {hydrated && played ? (
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 size={11} aria-hidden="true" />
              You played today - come back tomorrow
            </span>
          ) : (
            "A new element fact + 3 quick questions every day."
          )}
        </div>
      </div>

      <ArrowRight
        size={16}
        className="flex-shrink-0 text-muted-foreground group-hover:text-primary transition-colors"
        aria-hidden="true"
      />
    </Link>
  );
}